import { InspectionResultsApi } from '../../api-generated';
import { apiConfig } from '../../api-generated/apiConfig';
/**
 * Results list page.
 *
 * Displays historical inspection results in a FilterableDataTable with columns:
 * Machine, Template, Completed, Operator, Status, Score, Actions.
 *
 * Default sort: completedAt DESC (most recent first).
 * Filters: machine (select), dateRange (daterange), status (multiselect).
 * Row click opens a DrawerPanel with full inspection answers.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridColDef } from '@mui/x-data-grid';
import {
  FilterableDataTable,
  DrawerPanel,
  StatusBadge,
  PageContainer,
  type FilterConfig,
  type ActiveFilter,
  type RowAction,
  type HealthStatus,
} from '../../components/shared';

// ─── Types ───────────────────────────────────────────────────────────────────

/** A single answer within an inspection result. */
interface Answer {
  sectionId: number;
  question: string;
  answer: string;
  passed: boolean;
}

/** Shape of a result as returned by the API. */
interface ResultApiItem {
  id: number;
  machineId: number;
  machineName: string;
  templateId: number;
  templateName: string;
  completedAt: string;
  operatorName: string;
  status: HealthStatus;
  answers: Answer[];
}

/** Shape of a result row for the table. */
interface ResultRow {
  id: number;
  machineName: string;
  templateName: string;
  completedAt: string;
  operatorName: string;
  status: HealthStatus;
  passedCount: number;
  totalCount: number;
  answers: Answer[];
}

/** Backend paginated response shape for /api/results. */
interface ResultsApiResponse {
  content: ResultApiItem[];
  totalElements: number;
}

// ─── API Fetcher ─────────────────────────────────────────────────────────────

interface FetchResultsParams {
  page: number;
  size: number;
  sort: string;
  filters: ActiveFilter[];
}

async function fetchResults(params: FetchResultsParams): Promise<ResultsApiResponse> {
  const { page, size, sort, filters } = params;
  const searchParams = new URLSearchParams({
    page: String(page),
    size: String(size),
    sort,
  });

  // Apply filters to query params
  for (const filter of filters) {
    if (filter.field === 'machine' && filter.value) {
      searchParams.set('machineId', String(filter.value));
    }
    if (filter.field === 'dateRange' && Array.isArray(filter.value)) {
      const [from, to] = filter.value as [string, string];
      if (from) searchParams.set('dateFrom', from);
      if (to) searchParams.set('dateTo', to);
    }
    if (filter.field === 'status' && Array.isArray(filter.value)) {
      const statuses = filter.value as string[];
      if (statuses.length > 0) {
        searchParams.set('status', statuses.join(','));
      }
    }
  }

  const resultsApi = new InspectionResultsApi(apiConfig);
  return resultsApi.queryResults({
    machineId: searchParams.get('machineId') || undefined,
    page: Number(searchParams.get('page')) || undefined,
    size: Number(searchParams.get('size')) || undefined,
    sort: searchParams.get('sort') || undefined,
    status: searchParams.get('status') || undefined,
  } as any) as any;
}

// ─── Score helpers ───────────────────────────────────────────────────────────

/**
 * Returns a color for the score based on pass ratio.
 * All passed = green, majority failed = red, some failed = amber.
 */
function getScoreColor(passed: number, total: number): string {
  if (total === 0) return 'text.secondary';
  if (passed === total) return 'success.main';
  if (passed < total / 2) return 'error.main';
  return 'warning.main';
}

// ─── Filter Configuration ────────────────────────────────────────────────────

const FILTER_CONFIGS: FilterConfig[] = [
  {
    field: 'machine',
    label: 'Machine',
    type: 'select',
    options: [], // Will be populated dynamically in a future enhancement
  },
  {
    field: 'dateRange',
    label: 'Date',
    type: 'daterange',
  },
  {
    field: 'status',
    label: 'Status',
    type: 'multiselect',
    options: [
      { value: 'HEALTHY', label: 'Passed' },
      { value: 'WARNING', label: 'Partial' },
      { value: 'CRITICAL', label: 'Failed' },
    ],
  },
];

// ─── Column Definitions ──────────────────────────────────────────────────────

const columns: GridColDef[] = [
  {
    field: 'machineName',
    headerName: 'Machine',
    flex: 1,
    minWidth: 140,
  },
  {
    field: 'templateName',
    headerName: 'Template',
    flex: 1,
    minWidth: 140,
  },
  {
    field: 'completedAt',
    headerName: 'Completed',
    flex: 0.8,
    minWidth: 130,
    renderCell: (params) => {
      if (!params.value) return '—';
      const date = new Date(params.value as string);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  {
    field: 'operatorName',
    headerName: 'Operator',
    flex: 0.7,
    minWidth: 120,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 110,
    renderCell: (params) => (
      <StatusBadge status={params.value as HealthStatus} />
    ),
  },
  {
    field: 'score',
    headerName: 'Score',
    flex: 0.5,
    minWidth: 100,
    sortable: false,
    renderCell: (params) => {
      const row = params.row as ResultRow;
      const color = getScoreColor(row.passedCount, row.totalCount);
      return (
        <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
          {row.totalCount > 0 ? `${row.passedCount}/${row.totalCount} passed` : '—'}
        </Typography>
      );
    },
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const ResultsPage: React.FC = () => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<ResultRow | null>(null);

  // ─── Data fetching ───────────────────────────────────────────────────────
  const resultsQuery = useQuery<ResultsApiResponse, Error>({
    queryKey: ['results', 'list', page, pageSize, activeFilters],
    queryFn: () =>
      fetchResults({
        page,
        size: pageSize,
        sort: 'completedAt,desc',
        filters: activeFilters,
      }),
  });

  // Transform API data into row objects
  const rows: ResultRow[] = useMemo(() => {
    const content = resultsQuery.data?.content ?? [];
    return content.map((item: ResultApiItem) => {
      const passedCount = item.answers.filter((a: Answer) => a.passed).length;
      const totalCount = item.answers.length;
      return {
        id: item.id,
        machineName: item.machineName,
        templateName: item.templateName,
        completedAt: item.completedAt,
        operatorName: item.operatorName,
        status: item.status,
        passedCount,
        totalCount,
        answers: item.answers,
      };
    });
  }, [resultsQuery.data]);

  const totalCount = resultsQuery.data?.totalElements ?? 0;

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleRowClick = useCallback((row: ResultRow) => {
    setSelectedResult(row);
    setDrawerOpen(true);
  }, []);

  const handleFilterChange = useCallback((filters: ActiveFilter[]) => {
    setActiveFilters(filters);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedResult(null);
  }, []);

  // ─── Row actions ─────────────────────────────────────────────────────────

  const rowActions: RowAction<ResultRow>[] = useMemo(
    () => [
      {
        label: 'View Details',
        icon: VisibilityIcon,
        onClick: (row) => {
          setSelectedResult(row);
          setDrawerOpen(true);
        },
      },
    ],
    [],
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Results" subtitle="View completed inspection results">
      <FilterableDataTable<ResultRow>
        rows={rows}
        columns={columns}
        loading={resultsQuery.isLoading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        filters={FILTER_CONFIGS}
        onFilterChange={handleFilterChange}
        onRowClick={handleRowClick}
        actions={rowActions}
        searchPlaceholder="Search results…"
      />

      {/* Detail Drawer */}
      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        title="Inspection Result"
        width="medium"
      >
        {selectedResult && (
          <Box>
            {/* Result metadata */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Machine
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {selectedResult.machineName}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Template
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {selectedResult.templateName}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Operator
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {selectedResult.operatorName}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Completed
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {new Date(selectedResult.completedAt).toLocaleString()}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Score
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: getScoreColor(selectedResult.passedCount, selectedResult.totalCount),
                  fontWeight: 500,
                }}
              >
                {selectedResult.passedCount}/{selectedResult.totalCount} passed
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Answers list */}
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Inspection Answers
            </Typography>
            <List disablePadding>
              {selectedResult.answers.map((answer) => (
                <ListItem
                  key={answer.sectionId}
                  sx={{ px: 0, py: 0.75, alignItems: 'flex-start' }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    {answer.passed ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={answer.question}
                    secondary={answer.answer}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DrawerPanel>
    </PageContainer>
  );
};

export default ResultsPage;
